<!-- src/views/teacher/TeacherDashboard/DivWrapperScreen/sections/FrameSubsection.vue -->
<template>
  <div class="dashboard-section">
    <!-- Левая часть - Курсы -->
    <div class="courses-container">
      <CoursesContainer />
    </div>
    <!-- Правая часть - Панели -->
    <aside class="sidebar-panels">
      <!-- Календарь с точками конференций -->
      <section class="calendar-panel">
        <div class="calendar-header">
          <button @click="previousMonth" class="calendar-nav">‹</button>
          <div class="calendar-title">
            <span class="month">{{ monthName }}</span>
            <span class="year">{{ currentYear }}</span>
          </div>
          <button @click="nextMonth" class="calendar-nav">›</button>
        </div>
        <div class="calendar-grid">
          <div class="weekdays">
            <span v-for="day in weekDays" :key="day">{{ day }}</span>
          </div>
          <div class="dates">
            <button
              v-for="date in calendarDates"
              :key="date.key"
              class="date-cell"
              :class="{
                today: date.isToday,
                selected: isSelected(date),
                'other-month': date.isOtherMonth,
                'has-events': date.conferences.length > 0
              }"
              @click="selectDate(date)"
            >
              <!-- Число -->
              <span class="day-num">{{ date.day }}</span>
              <!--  Цветные точки ПОД числом -->
              <div class="conf-dots" v-if="date.conferences.length > 0">
                <span
                  v-for="(conf, i) in date.conferences.slice(0, 3)"
                  :key="i"
                  class="conf-dot"
                  :style="{ backgroundColor: conf.color || getDefaultColor(i) }"
                  :class="getDotClass(conf)"
                ></span>
                <span v-if="date.conferences.length > 3" class="more-dots">
                  +{{ date.conferences.length - 3 }}
                </span>
              </div>
            </button>
          </div>
        </div>
        <!--  ЛЕГЕНДА: Список ближайших конференций -->
        <div class="upcoming-legend">
          <h4 class="legend-title">Ближайшие конференции</h4>
          
          <div v-if="upcomingConferences.length === 0" class="no-conferences">
            <span>Нет запланированных конференций</span>
          </div>
          <div v-else class="legend-list">
            <div 
              v-for="conf in upcomingConferences" 
              :key="conf.id"
              class="legend-item"
              :class="{ 'is-live': isLive(conf), 'is-soon': isSoon(conf) }"
            >
              <!-- Цветная точка -->
              <span 
                class="legend-dot" 
								:style="{ backgroundColor: conf.color || '#0096ed' }"
                :class="{ pulse: isLive(conf) }"
              ></span>
              <!-- Дата и время -->
              <div class="legend-datetime">
                <span class="legend-date">{{ formatDateShort(conf.scheduled_at) }}</span>
                <span class="legend-time">{{ formatTime(conf.scheduled_at) }}</span>
              </div>
              <!-- Название курса -->
              <span class="legend-course" :title="conf.courseTitle">
                {{ conf.courseTitle || 'Курс' }}
              </span>
              <!-- Статус -->
              <span class="legend-status" :class="getStatusClass(conf)">
                {{ getStatusLabel(conf) }}
              </span>
            </div>
          </div>
        </div>
        <!--  Детали выбранного дня -->
        <transition name="slide-up">
          <div v-if="selectedDayConferences.length" class="selected-day">
            <h4 class="selected-day-title">
              <span>{{ formatSelectedDate }}</span>
              <button class="close-selected" @click="selectedDate = null">×</button>
            </h4>
            <div class="day-events">
              <div
                v-for="conf in selectedDayConferences"
                :key="conf.id"
                class="day-event"
                :class="{ live: isLive(conf), ended: isEnded(conf) }"
              >
                <span 
                  class="ev-dot" 
								:style="{ backgroundColor: conf.color || '#0096ed' }"
                ></span>
                <div class="ev-info">
                  <span class="ev-time">{{ formatTime(conf.scheduled_at) }}</span>
                  <span class="ev-course">{{ conf.courseTitle || 'Курс' }}</span>
                </div>
                <span class="ev-title" :title="conf.title">{{ conf.title }}</span>
                <span class="ev-status" :class="getStatusClass(conf)">
                  {{ getStatusLabel(conf) }}
                </span>
              </div>
            </div>
          </div>
        </transition>
      </section>
      <section class="homework-panel">
        <div class="homework-panel__header">
          <h3 class="homework-panel__title">Оповещение о ДЗ</h3>
          <button
            class="homework-panel__action"
            type="button"
            :disabled="homeworkItems.length === 0"
            @click="goToHomeworkReview"
          >
            Перейти к проверке
          </button>
        </div>
        <div v-if="homeworkLoading" class="homework-panel__state">
          Загрузка...
        </div>
        <div v-else-if="homeworkError" class="homework-panel__state">
          Не удалось загрузить отправки
        </div>
        <div v-else-if="homeworkItems.length === 0" class="homework-panel__state">
          Новых отправок пока нет
        </div>
        <div v-else class="homework-panel__list">
          <div
            v-for="item in homeworkItems"
            :key="item.id"
            class="homework-panel__item"
            @click="openHomeworkSubmission(item)"
          >
            <div class="homework-panel__avatar">
              <img
                v-if="item.studentAvatar"
                :src="item.studentAvatar"
                :alt="item.studentName"
              />
              <span v-else>{{ getStudentInitials(item.studentName) }}</span>
            </div>
            <div class="homework-panel__info">
              <div class="homework-panel__course">
                Курс: {{ item.courseTitle }}
                <span v-if="item.moduleTitle">. {{ item.moduleTitle }}</span>
              </div>
              <div class="homework-panel__message">
                <strong>{{ item.studentName }}</strong> отправил(а) задание
              </div>
            </div>
            <div class="homework-panel__time">
              {{ formatHomeworkTime(item.submittedAt) }}
            </div>
          </div>
        </div>
      </section>
      <section v-if="false" class="comments-panel"></section>
    </aside>
  </div>
</template>

<script>
import CoursesContainer from './СoursesСontainer.vue'
import { mapState } from 'pinia'
import { useConferencesStore } from '@/stores/conferences'

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

// Палитра цветов для курсов
const COLORS = [
	'#0096ed', '#00B894', '#E17055', '#0984E3',
	'#FDCB6E', '#E84393', '#00CEC9', '#FF7675',
	'#74B9FF', '#0096ed'
]

export default {
  name: 'FrameSubsection',
  components: {
    CoursesContainer
  },
  props: {
    // Конференции с цветами от DivSubsection (опционально)
    conferencesWithColors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: null,
      weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
      homeworkSubmissions: [],
      homeworkLoading: false,
      homeworkError: null,
      // Для автообновления статусов
      now: Date.now(),
      updateInterval: null
    }
  },
  computed: {
    ...mapState(useConferencesStore, {
      storeConferences: state => state.teacherConferences
    }),

    // Получаем конференции с цветами (из props или из store)
    conferences() {
      // Приоритет: props -> store
      if (this.conferencesWithColors && this.conferencesWithColors.length > 0) {
        return this.conferencesWithColors
      }

      // Если props пустой - берём из store и добавляем цвета
      const result = []
      const storeData = this.storeConferences || []

      if (Array.isArray(storeData)) {
        storeData.forEach((group, groupIndex) => {
          const color = this.getCourseColor(group.course_id, groupIndex)
          
          if (group.conferences && Array.isArray(group.conferences)) {
            group.conferences.forEach(conf => {
              result.push({
                ...conf,
                color,
                courseId: group.course_id,
                courseTitle: group.course_title
              })
            })
          }
        })
      }

      return result
    },

    /**
     * ✅ ИСПРАВЛЕНО: Ближайшие конференции с инлайн-проверкой
     * Vue корректно трекает this.now как зависимость
     */
    upcomingConferences() {
      // Явная зависимость от this.now для реактивности
      const now = this.now
      
      return this.conferences
        .filter(conf => {
          if (!conf.scheduled_at) return false
          if (conf.status === 'cancelled') return false
          
          // Инлайн проверка isEnded (для реактивности)
          // Статус явно завершён
          if (conf.status === 'ended' || conf.status === 'completed') return false
          
          // Проверяем время окончания
          const scheduled = new Date(conf.scheduled_at).getTime()
          const duration = (conf.duration_minutes || 60) * 60 * 1000
          const endTime = scheduled + duration
          
          // Если время окончания прошло - не показываем
          if (now >= endTime) return false
          
          return true
        })
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
        .slice(0, 5)
    },

    homeworkItems() {
      return this.homeworkSubmissions.slice(0, 6)
    },

    currentYear() {
      return this.currentDate.getFullYear()
    },

    currentMonth() {
      return this.currentDate.getMonth()
    },

    monthName() {
      return MONTHS[this.currentMonth]
    },

    calendarDates() {
      const dates = []
      const year = this.currentYear
      const month = this.currentMonth
      const today = new Date()

      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      let startDay = firstDay.getDay() - 1
      if (startDay < 0) startDay = 6

      // Предыдущий месяц
      const prevLast = new Date(year, month, 0).getDate()
      for (let i = startDay - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, prevLast - i)
        dates.push(this.makeDateObj(d, today, true))
      }

      // Текущий месяц
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const d = new Date(year, month, day)
        dates.push(this.makeDateObj(d, today, false))
      }

      // Следующий месяц
      const rem = 42 - dates.length
      for (let day = 1; day <= rem; day++) {
        const d = new Date(year, month + 1, day)
        dates.push(this.makeDateObj(d, today, true))
      }

      return dates
    },

    selectedDayConferences() {
      if (!this.selectedDate) return []
      const dateStr = this.toDateString(this.selectedDate)
      return this.conferences
        .filter(c => {
          if (!c.scheduled_at) return false
          return this.toDateString(new Date(c.scheduled_at)) === dateStr
        })
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    },

    formatSelectedDate() {
      if (!this.selectedDate) return ''
      return this.selectedDate.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        weekday: 'long'
      })
    }
  },

  methods: {
    async loadHomeworkSubmissions() {
      this.homeworkLoading = true
      this.homeworkError = null

      try {
        const { coursesService, modulesService, homeworkService } = await import('@/services')
        let courses = []

        try {
          courses = await coursesService.getTeacherCourses()
        } catch (error) {
          if (typeof coursesService.getMy === 'function') {
            courses = await coursesService.getMy()
          }
        }

        const normalizedCourses = Array.isArray(courses)
          ? courses
          : courses?.courses || []

        if (!normalizedCourses.length) {
          this.homeworkSubmissions = []
          return
        }

        const modulesByCourse = await Promise.all(
          normalizedCourses.map(async course => {
            const modules = await modulesService.getByCourseId(course.id).catch(() => [])
            const list = Array.isArray(modules) ? modules : modules?.modules || []
            const courseTitle =
              course.title || course.name || course.course_title || 'Курс'

            return list.map(module => ({
              ...module,
              courseId: course.id,
              courseTitle,
            }))
          })
        )

        const modules = modulesByCourse.flat()
        if (!modules.length) {
          this.homeworkSubmissions = []
          return
        }

        const homeworkByModule = await Promise.all(
          modules.map(async module => {
            const homework = await homeworkService
              .getByModuleId(module.id, true)
              .catch(() => [])
            const list = Array.isArray(homework)
              ? homework
              : homework?.homework || homework?.items || []
            const moduleTitle = module.title || module.name || 'Модуль'

            return list.map(item => ({
              ...item,
              moduleTitle,
              courseTitle: module.courseTitle,
              courseId: module.courseId,
            }))
          })
        )

        const homeworks = homeworkByModule.flat()
        if (!homeworks.length) {
          this.homeworkSubmissions = []
          return
        }

        const submissionsByHomework = await Promise.all(
          homeworks.map(async homework => {
            const submissions = await homeworkService
              .getAllSubmissions(homework.id)
              .catch(() => [])
            const list = Array.isArray(submissions)
              ? submissions
              : submissions?.submissions || submissions?.data || []

            return list.map(submission =>
              this.normalizeHomeworkSubmission(submission, homework)
            )
          })
        )

        const submissions = submissionsByHomework.flat().filter(Boolean)
        this.homeworkSubmissions = submissions
          .filter(item => item.status === 'pending')
          .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
      } catch (error) {
        console.error('[HomeworkPanel] Failed to load submissions:', error)
        this.homeworkError = 'load_failed'
        this.homeworkSubmissions = []
      } finally {
        this.homeworkLoading = false
      }
    },

    normalizeHomeworkSubmission(submission, homework) {
      const status = String(submission?.status || '').toLowerCase()
      const normalizedStatus = status === 'submitted' ? 'pending' : status
      const submittedAt =
        submission?.submitted_at ||
        submission?.submittedAt ||
        submission?.created_at ||
        submission?.createdAt ||
        null
      const normalizeFiles = (value) => {
        if (!value) return []
        if (Array.isArray(value)) return value
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : parsed ? [parsed] : []
          } catch (error) {
            return value ? [value] : []
          }
        }
        return value ? [value] : []
      }
      const attachments = normalizeFiles(
        submission?.files || submission?.attachments || submission?.materials
      )
      const revisionAttachments = normalizeFiles(
        submission?.revision_submission_files || submission?.revisionSubmissionFiles
      )
      const hasPayload =
        Boolean(String(submission?.submission_text ?? submission?.submissionText ?? '').trim()) ||
        attachments.length > 0 ||
        Boolean(
          String(
            submission?.revision_submission_text ??
              submission?.revisionSubmissionText ??
              ''
          ).trim()
        ) ||
        revisionAttachments.length > 0
      const isSystemState =
        ['graded', 'needs_revision'].includes(String(normalizedStatus || '').toLowerCase()) ||
        submission?.score !== null && submission?.score !== undefined
      if (!hasPayload && !isSystemState) return null

      return {
        id: submission?.id || `${homework.id}-${submission?.student_id || '0'}-${submittedAt || 'n/a'}`,
        homeworkId: homework.id,
        homeworkTitle: homework.title || 'Домашнее задание',
        moduleTitle: homework.moduleTitle,
        courseTitle: homework.courseTitle,
        courseId: homework.courseId,
        studentName:
          submission?.student_name ||
          submission?.studentName ||
          submission?.student_full_name ||
          submission?.studentEmail ||
          'Студент',
        studentAvatar:
          submission?.student_avatar ||
          submission?.studentAvatar ||
          submission?.avatar_url ||
          null,
        submittedAt,
        status: normalizedStatus || 'pending',
      }
    },

    getStudentInitials(name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (parts.length === 0) return '?'
      const first = parts[0]?.[0] || ''
      const second = parts.length > 1 ? parts[1]?.[0] || '' : ''
      return `${first}${second}`.toUpperCase()
    },

    openHomeworkSubmission(item) {
      if (!item?.homeworkId) return
      const query = item?.id ? { submissionId: item.id } : {}
      this.$router.push({
        path: `/teacher/homework/${item.homeworkId}/review`,
        query,
      })
    },

    goToHomeworkReview() {
      const firstItem = this.homeworkItems[0]
      if (firstItem) {
        this.openHomeworkSubmission(firstItem)
      }
    },

    formatHomeworkTime(dateStr) {
      return this.formatTime(dateStr)
    },

    // Цвет курса по ID
    getCourseColor(courseId, fallbackIndex = 0) {
      if (courseId) {
        const hash = String(courseId)
          .split('')
          .reduce((a, c) => a + c.charCodeAt(0), 0)
        return COLORS[hash % COLORS.length]
      }
      return COLORS[fallbackIndex % COLORS.length]
    },

    getDefaultColor(index) {
      return COLORS[index % COLORS.length]
    },

    toDateString(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    makeDateObj(date, today, isOtherMonth) {
      const key = this.toDateString(date)
      const conferences = this.getConfsForDate(date)
      return {
        date,
        key,
        day: date.getDate(),
        isToday: this.isSameDay(date, today),
        isOtherMonth,
        conferences
      }
    },

    getConfsForDate(date) {
      const dateStr = this.toDateString(date)
      return this.conferences.filter(c => {
        if (!c.scheduled_at) return false
        const confDate = new Date(c.scheduled_at)
        return this.toDateString(confDate) === dateStr
      })
    },

    isSameDay(d1, d2) {
      return d1.getDate() === d2.getDate() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getFullYear() === d2.getFullYear()
    },

    /**
     * Проверка "в эфире"
     */
    isLive(conf) {
      if (conf.status === 'completed' || conf.status === 'ended' || conf.status === 'cancelled') return false
      if (conf.status === 'live' || conf.status === 'in_progress') return true
      
      const now = this.now
      const scheduled = new Date(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      const endTime = scheduled + duration
      
      return now >= scheduled && now < endTime
    },

    /**
     * Проверка "завершена"
     */
    isEnded(conf) {
      if (conf.status === 'ended' || conf.status === 'completed') return true
      if (conf.status === 'cancelled') return false
      if (this.isLive(conf)) return false
      
      const now = this.now
      const scheduled = new Date(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      const endTime = scheduled + duration
      
      return now >= endTime
    },

    /**
     * Проверка "скоро начнётся"
     */
    isSoon(conf) {
      if (this.isLive(conf)) return false
      if (this.isEnded(conf)) return false
      if (conf.status === 'cancelled') return false
      
      const diff = new Date(conf.scheduled_at).getTime() - this.now
      return diff <= 60 * 60 * 1000 && diff > 0
    },

    /**
     * CSS класс для точки
     */
    getDotClass(conf) {
      if (this.isLive(conf)) return 'pulse'
      if (conf.status === 'cancelled') return 'cancelled'
      if (this.isEnded(conf)) return 'ended'
      if (this.isSoon(conf)) return 'soon'
      return ''
    },

    /**
     * CSS класс для статуса
     */
    getStatusClass(conf) {
      if (this.isLive(conf)) return 'live'
      if (conf.status === 'cancelled') return 'cancelled'
      if (this.isEnded(conf)) return 'ended'
      if (this.isSoon(conf)) return 'soon'
      return 'scheduled'
    },

    /**
     * Текст статуса
     */
    getStatusLabel(conf) {
      if (this.isLive(conf)) return 'В эфире'
      if (conf.status === 'cancelled') return 'Отменено'
      if (this.isEnded(conf)) return 'Завершено'
      if (this.isSoon(conf)) return 'Скоро'
      return 'Запланировано'
    },

    formatTime(dateStr) {
      if (!dateStr) return '--:--'
      return new Date(dateStr).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      if (this.isSameDay(date, today)) return 'Сегодня'
      if (this.isSameDay(date, tomorrow)) return 'Завтра'

      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short'
      })
    },

    previousMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth - 1, 1)
    },

    nextMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth + 1, 1)
    },

    selectDate(dateObj) {
      if (this.selectedDate && this.isSameDay(dateObj.date, this.selectedDate)) {
        this.selectedDate = null
        return
      }
      this.selectedDate = dateObj.date
      if (dateObj.isOtherMonth) {
        this.currentDate = new Date(dateObj.date.getFullYear(), dateObj.date.getMonth(), 1)
      }
    },

    isSelected(dateObj) {
      if (!this.selectedDate) return false
      return this.isSameDay(dateObj.date, this.selectedDate)
    },

    goToConference(conf) {
      this.$router.push(`/conference/${conf.id}`)
    },

    updateNow() {
      this.now = Date.now()
    }
  },

  mounted() {
    console.log('📅 FrameSubsection mounted')
    console.log('📊 Conferences from props:', this.conferencesWithColors?.length || 0)
    console.log('📊 Conferences from store:', this.storeConferences?.length || 0)
    console.log('📊 Total conferences:', this.conferences?.length || 0)

    this.loadHomeworkSubmissions()

    // Обновляем время каждую минуту для пересчёта статусов
    this.updateInterval = setInterval(this.updateNow, 60000)
  },

  beforeUnmount() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },

  watch: {
    conferences: {
      handler(newVal) {
        console.log('📅 Conferences updated:', newVal?.length || 0)
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
/* Основной контейнер */
.dashboard-section {
  display: flex;
  gap: 34px;
  padding: 20px;
  max-width: 1348px;
  margin: 0 auto;
  margin-bottom: 100px;
}

.courses-container {
  flex: 1;
}

.sidebar-panels {
  width: 345px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Панель календаря */
.calendar-panel {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.homework-panel {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.homework-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.homework-panel__title {
  margin: 0;
  font-family: 'Poppins', Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #191919;
}

.homework-panel__action {
  border: 1px solid #1f6bff;
  background: #ffffff;
  color: #1f6bff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.homework-panel__action:hover:not(:disabled) {
  background: #1f6bff;
  color: #ffffff;
}

.homework-panel__action:disabled {
  opacity: 0.5;
  cursor: default;
}

.homework-panel__state {
  padding: 20px 12px;
  text-align: center;
  font-size: 13px;
  color: #888;
}

.homework-panel__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 4px;
}

.homework-panel__item {
  display: grid;
  grid-template-columns: 42px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.homework-panel__item:hover {
  background: #f0f0f0;
}

.homework-panel__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f1f1f1;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  overflow: hidden;
}

.homework-panel__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.homework-panel__info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.homework-panel__course {
  font-size: 12px;
  font-weight: 600;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.homework-panel__message {
  font-size: 13px;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.homework-panel__message strong {
  color: #222;
}

.homework-panel__time {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.homework-panel__list::-webkit-scrollbar {
  width: 4px;
}

.homework-panel__list::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.calendar-nav {
  width: 32px;
  height: 32px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-nav:hover {
	background: #0096ed;
  color: #fff;
}

.calendar-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.month {
  font-family: 'Poppins', Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #191919;
}

.year {
  font-family: 'Poppins', Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #888;
}

.calendar-grid {
  margin-bottom: 16px;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.weekdays span {
  font-family: 'Roboto', Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: #999;
  text-transform: uppercase;
}

.dates {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

/*  Ячейка даты */
.date-cell {
  aspect-ratio: 1;
  min-height: 42px;
  border-radius: 10px;
  border: none;
  background: none;
  font-family: 'Roboto', Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #191919;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 4px 4px;
  gap: 3px;
}

.date-cell:hover {
  background-color: #f5f5f5;
}

.date-cell.today {
	background-color: #0096ed;
}

.date-cell.today .day-num {
  color: #ffffff;
}

.date-cell.today .conf-dots .conf-dot {
  border: 1px solid #ffffff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6);
}

.date-cell.today .conf-dots .more-dots {
  color: #ffffff;
}

.date-cell.selected {
	background: #eaf6ff;
	box-shadow: inset 0 0 0 2px #0096ed;
}

.date-cell.other-month {
  opacity: 0.3;
}

.date-cell.has-events {
  font-weight: 700;
}

.day-num {
  line-height: 1;
  font-size: 14px;
}

/*  Точки конференций ПОД числом */
.conf-dots {
  display: flex;
  gap: 3px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  min-height: 8px;
}

.conf-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.conf-dot.pulse {
  animation: dot-pulse 1.5s ease-in-out infinite;
}

.conf-dot.soon {
  animation: dot-glow 2s ease-in-out infinite;
}

.conf-dot.cancelled {
  opacity: 0.4;
}

/* ✅ НОВЫЙ СТИЛЬ: Точка для завершённых */
.conf-dot.ended {
  opacity: 0.5;
}

@keyframes dot-pulse {
  0%, 100% { 
    transform: scale(1); 
    opacity: 1; 
  }
  50% { 
    transform: scale(1.3); 
    opacity: 0.7; 
  }
}

@keyframes dot-glow {
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(243, 156, 18, 0.5); 
  }
  50% { 
    box-shadow: 0 0 4px 2px rgba(243, 156, 18, 0.3); 
  }
}

.more-dots {
  font-size: 8px;
  color: #888;
  font-weight: 600;
}

/*  ЛЕГЕНДА: Список ближайших конференций */
.upcoming-legend {
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.legend-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
}

.no-conferences {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 13px;
}

.legend-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.legend-item {
  display: grid;
  grid-template-columns: 10px 70px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.legend-item:hover {
  background: #f0f0f0;
}

.legend-item.is-live {
  background: linear-gradient(90deg, rgba(39, 174, 96, 0.1) 0%, #fafafa 100%);
  border-left: 3px solid #27ae60;
  padding-left: 9px;
}

.legend-item.is-soon {
  background: linear-gradient(90deg, rgba(243, 156, 18, 0.1) 0%, #fafafa 100%);
  border-left: 3px solid #f39c12;
  padding-left: 9px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-dot.pulse {
  animation: dot-pulse 1.5s ease-in-out infinite;
}

.legend-datetime {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.legend-date {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.legend-time {
  font-size: 11px;
  color: #888;
}

.legend-course {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-status {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.legend-status.live {
  background: #27ae60;
  color: #fff;
}

.legend-status.soon {
  background: #f39c12;
  color: #fff;
}

.legend-status.scheduled {
  background: #f0f0f0;
  color: #888;
}

.legend-status.ended {
  background: #f5f5f5;
  color: #aaa;
}

.legend-status.cancelled {
  background: #fff5f5;
  color: #e74c3c;
}

/*  Выбранный день */
.selected-day {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.selected-day-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px;
  color: #333;
}

.selected-day-title span {
  text-transform: capitalize;
}

.close-selected {
  width: 24px;
  height: 24px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-selected:hover {
  background: #e0e0e0;
  color: #333;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
}

.day-event {
  display: grid;
  grid-template-columns: 10px 70px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fafafa;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.day-event:hover {
  background: #f0f0f0;
}

.day-event.live {
  background: linear-gradient(90deg, rgba(39, 174, 96, 0.12) 0%, #fafafa 100%);
  border-left: 3px solid #27ae60;
  padding-left: 9px;
}

/* ✅ НОВЫЙ СТИЛЬ: Завершённые события в выбранном дне */
.day-event.ended {
  opacity: 0.7;
  background: #f8f8f8;
}

.ev-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ev-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ev-time {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.ev-course {
  font-size: 11px;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ev-title {
  font-size: 13px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ev-status {
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.ev-status.live {
  background: #27ae60;
  color: #fff;
}

.ev-status.soon {
  background: #f39c12;
  color: #fff;
}

.ev-status.scheduled {
  background: #f0f0f0;
  color: #888;
}

.ev-status.ended {
  background: #f5f5f5;
  color: #aaa;
}

.ev-status.cancelled {
  background: #fff5f5;
  color: #e74c3c;
}

/* Scrollbar для списков */
.legend-list::-webkit-scrollbar,
.day-events::-webkit-scrollbar {
  width: 4px;
}

.legend-list::-webkit-scrollbar-track,
.day-events::-webkit-scrollbar-track {
  background: transparent;
}

.legend-list::-webkit-scrollbar-thumb,
.day-events::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

/* Анимация */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ✅ Адаптивность */
@media (max-width: 1200px) {
  .dashboard-section {
    flex-direction: column;
  }
  
  .sidebar-panels {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
}

@media (max-width: 600px) {
  .calendar-panel {
    padding: 16px;
  }
  
  .date-cell {
    min-height: 36px;
    font-size: 12px;
  }
  
  .legend-item {
    grid-template-columns: 8px 60px 1fr auto;
    gap: 8px;
    padding: 8px 10px;
  }
  
  .day-event {
    grid-template-columns: 8px 60px 1fr auto;
    gap: 8px;
    padding: 10px;
  }

  .homework-panel {
    padding: 14px;
  }

  .homework-panel__item {
    grid-template-columns: 36px 1fr;
  }

  .homework-panel__time {
    display: none;
  }
}
</style>
